import React from 'react';
import { Helmet } from 'react-helmet';
import {
  PAGE_TITLES,
  META_DESCRIPTIONS,
  getOGImage,
  generateOpenGraphMeta,
  generateTwitterCardMeta
} from '../utils/seoUtils';

/**
 * SEOHead Component
 * Manages all SEO-related meta tags and structured data for pages
 */
const SEOHead = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  structuredData,
  pageType = 'default',
  noindex = false,
  nofollow = false
}) => {
  // Use defaults if not provided
  const finalTitle = title || PAGE_TITLES?.[pageType] || PAGE_TITLES?.home;
  const finalDescription = description || META_DESCRIPTIONS?.[pageType] || META_DESCRIPTIONS?.home;
  const finalOGImage = ogImage || getOGImage(pageType);
  const finalCanonicalUrl = canonicalUrl || window?.location?.href;

  // Generate meta tags
  const ogMeta = generateOpenGraphMeta(
    finalTitle,
    finalDescription,
    finalOGImage,
    finalCanonicalUrl
  );

  const twitterMeta = generateTwitterCardMeta(
    finalTitle,
    finalDescription,
    finalOGImage
  );

  // Combine robots meta
  const robotsContent = [
    noindex ? 'noindex' : 'index',
    nofollow ? 'nofollow' : 'follow',
    'max-image-preview:large',
    'max-snippet:-1',
    'max-video-preview:-1'
  ]?.join(', ');

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="title" content={finalTitle} />
      <meta name="description" content={finalDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robotsContent} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={finalCanonicalUrl} />
      
      {/* Open Graph Meta Tags */}
      {Object?.entries(ogMeta)?.map(([key, value]) => (
        <meta key={key} property={key} content={value} />
      ))}
      
      {/* Twitter Meta Tags */}
      {Object?.entries(twitterMeta)?.map(([key, value]) => (
        <meta key={key} name={key} content={value} />
      ))}
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON?.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;